import * as z from "zod";
import { userFacingError } from "../../authorization";
import type { ScopeKind } from "../permissions";
import { BUILTIN_ROLES, type BuiltinRoleId } from "../roles";

const SCOPE_KIND_PLURAL: Record<ScopeKind, string> = {
  tenant: "tenants",
  space: "spaces",
  connected_app: "connectors",
  skill: "skills",
  network: "networks",
  user_group: "user groups",
  service_account: "service accounts",
  oidc_provider: "OIDC providers",
};

export const grantEntry = z.object({
  userGroupId: z.string().optional(),
  userId: z.string().optional(),
  serviceAccountId: z.string().optional(),
  builtinRoleId: z.string().optional(),
  customRoleId: z.string().optional(),
});

export type GrantInput = z.infer<typeof grantEntry>;

// Accepts the same principal shape as other scopes so callers can send a
// userGroupId, which validation rejects until RBAC resolves nested groups.

export type UserGroupGrantInput = z.infer<typeof grantEntry>;

function assertPrincipalXor(grant: GrantInput): void {
  const count =
    (grant.userGroupId ? 1 : 0) +
    (grant.userId ? 1 : 0) +
    (grant.serviceAccountId ? 1 : 0);
  if (count !== 1) {
    throw userFacingError(
      "Each grant must specify exactly one of userGroupId, userId, or serviceAccountId"
    );
  }
}

function assertRoleXor(grant: GrantInput): void {
  if (!!grant.builtinRoleId === !!grant.customRoleId) {
    throw userFacingError(
      "Each grant must specify exactly one of builtinRoleId or customRoleId"
    );
  }
}

export function assertNoAllowAll(
  permissions: readonly (string | { permission: string })[]
): void {
  const has = permissions.some((p) =>
    typeof p === "string" ? p === "allow_all" : p.permission === "allow_all"
  );
  if (has) {
    throw userFacingError("Cannot assign a role with allow_all");
  }
}

export function assertBuiltinRoleScope(
  builtinRoleId: string,
  scopeKind: ScopeKind
): void {
  const role = BUILTIN_ROLES[builtinRoleId as BuiltinRoleId];
  if (!role || role.scopeKind !== scopeKind) {
    throw userFacingError(
      `Role "${builtinRoleId}" is not valid for ${SCOPE_KIND_PLURAL[scopeKind]}`
    );
  }
}

export function assertCustomRoleScope(
  role: { scopeKind: string },
  scopeKind: ScopeKind
): void {
  if (role.scopeKind !== scopeKind) {
    throw userFacingError(
      `Custom role is not valid for ${SCOPE_KIND_PLURAL[scopeKind]}`
    );
  }
}

export function validateGrantsInput(
  grants: readonly GrantInput[],
  scopeKind: ScopeKind
): void {
  for (const grant of grants) {
    assertPrincipalXor(grant);
    assertRoleXor(grant);
    if (grant.builtinRoleId) {
      assertBuiltinRoleScope(grant.builtinRoleId, scopeKind);
    }
  }
}

export function validateUserGroupGrants(
  grants: readonly UserGroupGrantInput[],
  scopeKind: ScopeKind
): void {
  for (const grant of grants) {
    if (grant.userGroupId) {
      throw userFacingError("User groups cannot be members of user groups");
    }
    if (!grant.userId) {
      throw userFacingError("Each member grant must specify a userId");
    }
    assertRoleXor(grant);
    if (grant.builtinRoleId) {
      assertBuiltinRoleScope(grant.builtinRoleId, scopeKind);
    }
  }
}
